import React, { useState, useEffect } from "react";
import { NavLink } from "react-router-dom";
import { Button, Spinner, Tooltip, useDisclosure } from "@heroui/react";
import useUserProfile from "../../hooks/useUserProfile";
import useAxiosPrivate from "@/hooks/useAxiosPrivate";
import useUser from "@/hooks/useUser";
import ArchiveModal from "../common/modal/ArchiveModal";

import { PiWarningFill } from "react-icons/pi";
import { IoIosCheckmarkCircle } from "react-icons/io";
import { GiSandsOfTime } from "react-icons/gi";
import { FiArchive } from "react-icons/fi";
import { BsInfoCircleFill } from "react-icons/bs";
import { useTranslation } from "react-i18next";

export default function StatusWarning({
  errorCount,
  status,
  loading,
  initialDataLoading,
  orderId,
  parsing,
  errors,
}) {
  const { t } = useTranslation();
  const { isDataReadOnlyOperators } = useUserProfile();
  const [initialErrorCount, setInitialErrorCount] = useState(null);
  const [ticketSent, setTicketSent] = useState(false);
  const [isSubmittingTicket, setIsSubmittingTicket] = useState(false);
  const axiosPrivate = useAxiosPrivate();

  const { data } = useUser();

  const hasProducts = Array.isArray(errors) && errors.some((error) => typeof error.index === "number");
  const { isOpen, onOpen, onClose } = useDisclosure();

  useEffect(() => {
    if (!initialDataLoading) {
      setInitialErrorCount(errorCount);
    }
  }, [initialDataLoading, errorCount]);

  if (initialDataLoading || initialErrorCount === null) {
    return <Spinner color="default" />;
  }

  const getTicketButtonContent = () => {
    if (isSubmittingTicket) {
      return (
        <div className="flex items-center">
          <Spinner size="sm" className="mr-1" />
          <span>Envoi...</span>
        </div>
      );
    }
    if (ticketSent) {
      return t("request_approved");
    }
    return t("improve_recognition");
  };

  const handleSendTicket = async () => {
    if (ticketSent) return;
    
    setIsSubmittingTicket(true);
    
    const ticketData = {
      subject: "Support parsing manuel",
      priority: 1,
      description: `
      <div style="font-family: Arial, sans-serif; line-height: 1.6;">
        <h2 style="color: #2563eb; border-bottom: 2px solid #e5e7eb; padding-bottom: 8px;">
          🔧 Demande de Support - Parsing Manuel
        </h2>

        <h3 style="color: #374151; margin-top: 24px;">📋 Informations Client</h3>
        <table style="border-collapse: collapse; width: 100%; margin-bottom: 16px;">
          <tr style="background-color: #f9fafb;">
            <td style="padding: 8px; border: 1px solid #e5e7eb; font-weight: bold;">Nom complet:</td>
            <td style="padding: 8px; border: 1px solid #e5e7eb;">${data?.user_given_name || "N/A"} ${data?.user_name || "N/A"}</td>
          </tr>
          <tr>
            <td style="padding: 8px; border: 1px solid #e5e7eb; font-weight: bold;">Email:</td>
            <td style="padding: 8px; border: 1px solid #e5e7eb;">${data?.user_email || "N/A"}</td>
          </tr>
          <tr style="background-color: #f9fafb;">
            <td style="padding: 8px; border: 1px solid #e5e7eb; font-weight: bold;">Company ID:</td>
            <td style="padding: 8px; border: 1px solid #e5e7eb;">${data?.organization_name || "N/A"}</td>
          </tr>
        </table>

        <h3 style="color: #374151; margin-top: 24px;">📦 Détails de la Commande</h3>
        <table style="border-collapse: collapse; width: 100%; margin-bottom: 16px;">
          <tr>
            <td style="padding: 8px; border: 1px solid #e5e7eb; font-weight: bold;">Job ID:</td>
            <td style="padding: 8px; border: 1px solid #e5e7eb;">${orderId || "N/A"}</td>
          </tr>
        </table>

        <div style="text-align: center; margin: 24px 0; padding: 16px; background-color: #f3f4f6; border-radius: 8px;">
          <a href="${globalThis.location.href}" 
             target="_blank" 
             style="display: inline-block; background-color: #2563eb; color: white; padding: 12px 24px; text-decoration: none; border-radius: 6px; font-weight: bold;">
            🔗 Accéder à la Commande
          </a>
        </div>
      </div>
    `,
    };

    try {
      await axiosPrivate.post(`/system/tickets`, ticketData);
      setTicketSent(true);
    } catch (error) {
      console.error("Erreur création ticket:", error);
    } finally {
      setIsSubmittingTicket(false);
    }
  };

  return (
    <>
      <div className="bg-[#f0f2f4] sticky top-20 z-50 w-full mr-3">
        {status === "pending_review" && !hasProducts && (
          <div className="bg-white rounded-xl w-full">
            <div className="p-4 flex  justify-between">
              <div className=" flex items-center">
                <div className="bg-violet-200 rounded-full outline-dashed outline-violet-500 p-2">
                  <FiArchive className="fill-violet-500 text-white/50 text-2xl" />
                </div>
                <div className="ml-3">{t("no_products_in_order")}</div>
              </div>
              <div className="flex">
                {!isDataReadOnlyOperators && (
                  <Button
                    type="button"
                    onPress={onOpen}
                    className="bg-violet-800 text-white rounded-md p-3"
                  >
                    {t("archive")}
                  </Button>
                )}
              </div>
            </div>
            <div className="bg-violet-200 text-black text-[11px] rounded-b-xl py-2 px-4 flex justify-between items-center">
              <div>
                <span className="text-violet-800 font-bold">{t("status")} :</span>{" "}
                {t("no_products_in_order")}
              </div>
            </div>
          </div>
        )}
        {status === "pending_review" && hasProducts && initialErrorCount > 0 && (
          <div className="bg-white rounded-xl w-full">
            <div className="p-4 flex  justify-between">
              <div className=" flex items-center">
                <div className="bg-vistactaorange/20 rounded-full outline-dashed outline-vistactaorange p-2">
                  <PiWarningFill className="fill-vistactaorange text-2xl" />
                </div>
                <div className="ml-3" data-cy="statusWarning__pendingReview-errorCount">
                  {errorCount > 1
                    ? `${errorCount} ${t("items_to_check")}`
                    : errorCount === 1
                      ? t("one_item_to_modify")
                      : t("ready_for_validation")}
                </div>
              </div>
              <div className="flex">
                {!isDataReadOnlyOperators && (
                  <Button
                    type="submit"
                    data-cy="statusWarning__pendingReview-submit-button"
                    className="bg-vistactaorange text-white rounded-md p-3"
                  >
                    {loading && <Spinner color="default" />}
                    {!loading && <div>{t("save_changes")}</div>}
                  </Button>
                )}
              </div>
            </div>
            <div className="bg-vistactaorange/30 text-black text-[11px] rounded-b-xl py-2 px-4 flex justify-between items-center">
              <div className="flex items-center">
                {!parsing && initialErrorCount > 15 && (
                  <>
                    <button
                      className={`border-2 rounded-md px-2 transition-all duration-200 ${
                        ticketSent 
                          ? 'border-gray-300 bg-gray-200 text-gray-500 cursor-not-allowed' 
                          : 'border-vistactaorange/30 bg-vistactaorange/30 cursor-pointer hover:bg-vistactaorange/40'
                      }`}
                      onClick={handleSendTicket}
                      aria-label="Manual parsing"
                    >
                      {getTicketButtonContent()}
                    </button>
                    <Tooltip
                      content={t("improve_recognition_details")}
                      showArrow={true}
                      placement="under"
                      delay={0}
                      closeDelay={0}
                      classNames={{
                        content: ["max-w-xs p-2 text-wrap text-xs"],
                      }}
                    >
                      <div className="ml-1">
                        <BsInfoCircleFill className={`h-6 ${ticketSent ? 'text-gray-400' : 'text-vistactaorange/70'}`} />
                      </div>
                    </Tooltip>
                  </>
                )}
              </div>
              <div>
                <span className="text-vistactaorange font-bold">{t("status")} :</span>{" "}
              {t("changes_pending")}
              </div>
            </div>
          </div>
        )}
        {status === "pending_review" && hasProducts && initialErrorCount === 0 && (
          <div className="bg-white rounded-xl w-full">
            <div className="p-4 flex  justify-between">
              <div className=" flex items-center">
                <div className="bg-vistawarningblue/20 rounded-full outline-dashed outline-vistawarningblue p-2">
                  <GiSandsOfTime className="fill-vistawarningblue text-2xl" />
                </div>
                <div className="ml-3">{t("manual_validation_required")}</div>
              </div>
              <div className="flex">
                {!isDataReadOnlyOperators && (
                  <Button type="submit" className="bg-vistawarningblue text-white rounded-md p-3">
                    {loading && <Spinner color="default" />}
                    {!loading && <div>{t("validate_order")}</div>}
                  </Button>
                )}
              </div>
            </div>
            <div className="bg-vistawarningblue/30 text-black text-[11px] rounded-b-xl py-2 px-4 flex justify-between items-center">
              
              <div>
                <span className="text-vistawarningblue font-bold">{t("status")} :</span>{" "}
                {t("order_requires_validation")}
              </div>
            </div>
          </div>
        )}
        {status === "success" && (
          <div className="bg-white rounded-xl w-full">
            <div className="p-4 flex  justify-between">
              <div className=" flex items-center">
                <div className="bg-vistagreen/20 rounded-full outline-dashed outline-vistagreen p-2">
                  <IoIosCheckmarkCircle className="fill-vistagreen text-2xl" />
                </div>
                <div className="ml-3">{t("order_validated")}</div>
              </div>
              <NavLink
                className="bg-vistagreen text-white rounded-md p-3"
                to={`/orders/?refresh=true`}
              >
                {t("back_to_control_tower")}
              </NavLink>
            </div>
            <div className="bg-vistagreen/30 text-black text-[11px] text-right rounded-b-xl py-2 px-4"></div>
          </div>
        )}
        {status === "archive" && (
          <div className="bg-white rounded-xl w-full">
            <div className="p-4 flex  justify-between">
              <div className=" flex items-center">
                <div className="bg-violet-200 rounded-full outline-dashed outline-violet-500 p-2">
                  <FiArchive className="fill-violet-500 text-white/50 text-2xl" />
                </div>
                <div className="ml-3">{t("order_archived")}</div>
              </div>
              <NavLink
                className="bg-violet-800 text-white rounded-md p-3"
                to={`/orders/?refresh=true`}
              >
                {t("back_to_control_tower")}
              </NavLink>
            </div>
            <div className="bg-violet-200 text-black text-[11px] text-right rounded-b-xl py-2 px-4"></div>
          </div>
        )}
      </div>
      <ArchiveModal
        isOpen={isOpen}
        onClose={onClose}
        orderId={orderId}
        redirect="true"
      />
    </>
  );
}